import React, { useState, useMemo } from 'react';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Box,
} from '@material-ui/core';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import ArrowForward from '@material-ui/icons/ArrowForward';
import UnfoldMore from '@material-ui/icons/UnfoldMore';
import { useStyles } from '../../../styles';
import type { IqeProject } from '../../../interfaces/ui';
import EnvironmentBadge from '../../../common/EnvironmentBadge';
import StatusBadge from '../../../common/StatusBadge';
import ProjectInfoPopover from '../../../common/ProjectInfoPopover';
import '../../../styles/projectsTable.css';

interface ProjectsTableProps {
  projects: IqeProject[];
  onNavigateToExecution: (projectId: string) => void;
}

type SortColumn =
  | 'name'
  | 'environment'
  | 'status'
  | 'lastRun'
  | 'testCases'
  | 'passRate'
  | 'failed';
type SortDirection = 'asc' | 'desc' | null;

export const ProjectsTable: React.FC<ProjectsTableProps> = ({
  projects,
  onNavigateToExecution,
}) => {
  const classes: any = useStyles();
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortColumn(null);
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedProjects = useMemo(() => {
    if (!sortColumn || !sortDirection) {
      return projects;
    }

    return [...projects].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'environment':
          aValue = a.environment?.toLowerCase() || '';
          bValue = b.environment?.toLowerCase() || '';
          break;
        case 'status':
          aValue = a.status?.toLowerCase() || '';
          bValue = b.status?.toLowerCase() || '';
          break;
        case 'lastRun':
          aValue = a.runs?.[0]?.startedAt
            ? new Date(a.runs[0].startedAt).getTime()
            : 0;
          bValue = b.runs?.[0]?.startedAt
            ? new Date(b.runs[0].startedAt).getTime()
            : 0;
          break;
        case 'testCases':
          aValue = a.runs?.[0]?.testCases || 0;
          bValue = b.runs?.[0]?.testCases || 0;
          break;
        case 'passRate':
          aValue = a.runs?.[0]?.passRate || 0;
          bValue = b.runs?.[0]?.passRate || 0;
          break;
        case 'failed':
          aValue = a.runs?.[0]?.failed || 0;
          bValue = b.runs?.[0]?.failed || 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [projects, sortColumn, sortDirection]);

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <TableContainer component={Paper} className="projects-table-container">
      <Table size="small" className={classes.table} aria-label="projects table">
        <TableHead>
          <TableRow classes={{ root: classes.tableHead }}>
            <TableCell
              classes={{ root: classes.tableHeadCell }}
              className="projects-table-info-cell"
            />
            <TableCell classes={{ root: classes.tableHeadCell }}>
              <TableSortLabel
                onClick={() => handleSort('name')}
                hideSortIcon={true}
              >
                <Box
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Typography variant="subtitle2">Project Name</Typography>
                  {sortColumn === 'name' ? (
                    <ArrowUpward
                      style={{
                        fontSize: 14,
                        opacity: 1,
                        transform:
                          sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  ) : (
                    <UnfoldMore style={{ fontSize: 14, opacity: 0.4 }} />
                  )}
                </Box>
              </TableSortLabel>
            </TableCell>
            <TableCell classes={{ root: classes.tableHeadCell }}>
              <TableSortLabel
                onClick={() => handleSort('environment')}
                hideSortIcon={true}
              >
                <Box
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Typography variant="subtitle2">Environment</Typography>
                  {sortColumn === 'environment' ? (
                    <ArrowUpward
                      style={{
                        fontSize: 14,
                        opacity: 1,
                        transform:
                          sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  ) : (
                    <UnfoldMore style={{ fontSize: 14, opacity: 0.4 }} />
                  )}
                </Box>
              </TableSortLabel>
            </TableCell>
            <TableCell classes={{ root: classes.tableHeadCell }}>
              <TableSortLabel
                onClick={() => handleSort('status')}
                hideSortIcon={true}
              >
                <Box
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Typography variant="subtitle2">Status</Typography>
                  {sortColumn === 'status' ? (
                    <ArrowUpward
                      style={{
                        fontSize: 14,
                        opacity: 1,
                        transform:
                          sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  ) : (
                    <UnfoldMore style={{ fontSize: 14, opacity: 0.4 }} />
                  )}
                </Box>
              </TableSortLabel>
            </TableCell>
            <TableCell classes={{ root: classes.tableHeadCell }}>
              <TableSortLabel
                onClick={() => handleSort('lastRun')}
                hideSortIcon={true}
              >
                <Box
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Typography variant="subtitle2">Last Run</Typography>
                  {sortColumn === 'lastRun' ? (
                    <ArrowUpward
                      style={{
                        fontSize: 14,
                        opacity: 1,
                        transform:
                          sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  ) : (
                    <UnfoldMore style={{ fontSize: 14, opacity: 0.4 }} />
                  )}
                </Box>
              </TableSortLabel>
            </TableCell>
            <TableCell classes={{ root: classes.tableHeadCell }}>
              <TableSortLabel
                onClick={() => handleSort('testCases')}
                hideSortIcon={true}
              >
                <Box
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Typography variant="subtitle2">Test Cases</Typography>
                  {sortColumn === 'testCases' ? (
                    <ArrowUpward
                      style={{
                        fontSize: 14,
                        opacity: 1,
                        transform:
                          sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  ) : (
                    <UnfoldMore style={{ fontSize: 14, opacity: 0.4 }} />
                  )}
                </Box>
              </TableSortLabel>
            </TableCell>
            <TableCell classes={{ root: classes.tableHeadCell }}>
              <TableSortLabel
                onClick={() => handleSort('passRate')}
                hideSortIcon={true}
              >
                <Box
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Typography variant="subtitle2">Pass Rate</Typography>
                  {sortColumn === 'passRate' ? (
                    <ArrowUpward
                      style={{
                        fontSize: 14,
                        opacity: 1,
                        transform:
                          sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  ) : (
                    <UnfoldMore style={{ fontSize: 14, opacity: 0.4 }} />
                  )}
                </Box>
              </TableSortLabel>
            </TableCell>
            <TableCell classes={{ root: classes.tableHeadCell }}>
              <TableSortLabel
                onClick={() => handleSort('failed')}
                hideSortIcon={true}
              >
                <Box
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <Typography variant="subtitle2">Failed</Typography>
                  {sortColumn === 'failed' ? (
                    <ArrowUpward
                      style={{
                        fontSize: 14,
                        opacity: 1,
                        transform:
                          sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  ) : (
                    <UnfoldMore style={{ fontSize: 14, opacity: 0.4 }} />
                  )}
                </Box>
              </TableSortLabel>
            </TableCell>
            <TableCell classes={{ root: classes.tableHeadCell }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedProjects
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map(project => (
              <TableRow
                key={project.id}
                classes={{ root: classes.configTableRow }}
              >
                <TableCell
                  classes={{ root: classes.tableBodyCell }}
                  className="projects-table-info-cell"
                >
                  <ProjectInfoPopover
                    projectId={project.id}
                    transactionId={project.transactionId}
                  />
                </TableCell>
                <TableCell classes={{ root: classes.tableBodyCell }}>
                  <Typography variant="subtitle2">{project.name}</Typography>
                </TableCell>
                <TableCell classes={{ root: classes.tableBodyCell }}>
                  <EnvironmentBadge environment={project.environment} />
                </TableCell>
                <TableCell classes={{ root: classes.tableBodyCell }}>
                  <StatusBadge status={project.status} />
                </TableCell>
                <TableCell classes={{ root: classes.tableBodyCell }}>
                  <Typography variant="subtitle2">
                    {formatDate(project.runs?.[0]?.startedAt)}
                  </Typography>
                </TableCell>
                <TableCell classes={{ root: classes.tableBodyCell }}>
                  <Typography variant="subtitle2">
                    {project.runs?.[0]?.testCases || '—'}
                  </Typography>
                </TableCell>
                <TableCell classes={{ root: classes.tableBodyCell }}>
                  <Typography variant="subtitle2">
                    {project.runs?.[0]?.passRate
                      ? `${project.runs[0].passRate}%`
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell classes={{ root: classes.tableBodyCell }}>
                  <Typography variant="subtitle2">
                    {project.runs?.[0]?.failed || '—'}
                  </Typography>
                </TableCell>
                <TableCell className={`${classes.flexEnd} ${classes.gap1}`}>
                  <IconButton
                    size="small"
                    onClick={() => onNavigateToExecution(project.id)}
                    title="View Execution Centre"
                    className="projects-table-nav-arrow-btn"
                  >
                    <ArrowForward />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          {sortedProjects.length === 0 && (
            <TableRow>
              <TableCell colSpan={9}>
                <Typography>No projects found.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[10, 25]}
        component="div"
        count={sortedProjects.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        classes={{
          root: classes.tablePaginationRoot,
          actions: classes.tablePaginationActions,
          selectRoot: classes.tablePaginationSelect,
          spacer: classes.tablePaginationBar,
        }}
      />
    </TableContainer>
  );
};
